<template>
    <AppLayout>
        <router-view />
    </AppLayout>

    <RouteSpinner
        v-if="store.getters.isRouting || store.getters.isRefreshing"
    />
</template>

<script setup lang="ts">
import { useStore } from 'vuex'
import RouteSpinner from '@/components/misc/RouteSpinner.vue'
import { onMounted, onBeforeUnmount, watch, ref } from 'vue'
import eventBus from '@/utils/eventBus'
import { useEventSource } from '@vueuse/core'
import { useRouter } from 'vue-router'

const store = useStore()
const router = useRouter()
const IDLE_TIMEOUT_MS = 30 * 60 * 1000
const IDLE_CHECK_INTERVAL_MS = 60 * 1000
const LAST_ACTIVITY_KEY = 'last_activity_ts'

const eventSource = (topics) => {
    if (!store.getters.eventSourceUrl) {
        throw 'No event source URL available'
    }

    const url = new URL(store.getters.eventSourceUrl)

    url.searchParams.append('authorization', store.getters.token)

    topics.forEach((topic) => {
        url.searchParams.append('topic', topic)
    })

    return useEventSource(
        url,
        ['message'], // named events
        {
            autoReconnect: {
                retries: 3,
                delay: 1000,
                onFailed() {
                    console.log('Failed to connect EventSource after 3 retries')
                },
            },
            serializer: {
                read: (rawData) => JSON.parse(rawData),
            },
        },
    )
}

const eventSourceClose = ref(null)
const idleCheckInterval = ref<number | null>(null)
let lastActivityWrite = 0

const touchActivity = (force = false) => {
    const now = Date.now()
    if (!force && now - lastActivityWrite < 30 * 1000) {
        return
    }
    lastActivityWrite = now
    localStorage.setItem(LAST_ACTIVITY_KEY, String(now))
}

const idleLogout = async () => {
    await store.dispatch('logout').catch((e) => e)
    router.push({ name: 'Login' })
}

const startIdleWatcher = () => {
    if (idleCheckInterval.value !== null) {
        return
    }

    // Start counting from now for a fresh authenticated session.
    touchActivity(true)

    idleCheckInterval.value = window.setInterval(() => {
        if (!store.getters.isAuthenticated) {
            return
        }

        const last = parseInt(
            localStorage.getItem(LAST_ACTIVITY_KEY) || '0',
            10,
        )
        const inactiveFor = Date.now() - (Number.isNaN(last) ? 0 : last)
        if (inactiveFor >= IDLE_TIMEOUT_MS) {
            idleLogout()
        }
    }, IDLE_CHECK_INTERVAL_MS)
}

const stopIdleWatcher = () => {
    if (idleCheckInterval.value !== null) {
        window.clearInterval(idleCheckInterval.value)
        idleCheckInterval.value = null
    }
    localStorage.removeItem(LAST_ACTIVITY_KEY)
}

const activityListener = () => {
    if (store.getters.isAuthenticated) {
        touchActivity()
    }
}

watch(
    () => [
        store.getters.eventSourceUrl,
        store.getters.authedMemberInfo?.id,
        store.getters.workspaceUuid,
        store.getters.isAuthenticated,
    ],
    (current, previous) => {
        if (
            store.getters.eventSourceUrl &&
            store.getters.authedMemberInfo?.id &&
            store.getters.workspaceUuid &&
            store.getters.isAuthenticated
        ) {
            let lastEventType

            const { event, status, data, close } = eventSource([
                'workspace-' + store.getters.workspaceUuid,
                'workspacemember-' + store.getters.authedMemberInfo.id,
            ])

            eventSourceClose.value = close

            // The 'event' ref holds the type of the last event received
            watch(event, (newEvent) => {
                lastEventType = newEvent
            })

            watch(data, (receivedData) => {
                // With named events, it seems that this watcher gets called once before lastEventType is updated, and once after.
                if (lastEventType) {
                    console.log('Received data:', receivedData)
                    eventBus.emit('sse.' + lastEventType, receivedData)
                }
            })

            // The 'status' ref indicates the connection state ('CONNECTING', 'OPEN', or 'CLOSED')
            watch(status, (newStatus) => {
                console.log('SSE connection:', newStatus)
            })
        } else if (eventSourceClose.value) {
            eventSourceClose.value()
            eventSourceClose.value = null
        }
    },
    { immediate: true },
)

watch(
    () => store.getters.isAuthenticated,
    (isAuthenticated) => {
        if (isAuthenticated) {
            startIdleWatcher()
            touchActivity(true)
        } else {
            stopIdleWatcher()
        }
    },
    { immediate: true },
)

// GLOBAL SEARCH INPUT HANDLER
onMounted(() => {
    // Listen for input events anywhere in the app
    document.addEventListener('input', (e) => {
        const el = e.target as HTMLInputElement
        if (el?.classList?.contains('search-field')) {
            el.classList.toggle('has-value', el.value.trim() !== '')
        }
    })

    // Handle already-filled fields on page load
    document.querySelectorAll('.search-field').forEach((el: any) => {
        el.classList.toggle('has-value', el.value.trim() !== '')
    })

    document.addEventListener('click', activityListener)
    document.addEventListener('keydown', activityListener)
    document.addEventListener('mousemove', activityListener)
    document.addEventListener('scroll', activityListener, { passive: true })
    document.addEventListener('touchstart', activityListener, { passive: true })
    document.addEventListener('visibilitychange', activityListener)
})

onBeforeUnmount(() => {
    document.removeEventListener('click', activityListener)
    document.removeEventListener('keydown', activityListener)
    document.removeEventListener('mousemove', activityListener)
    document.removeEventListener('scroll', activityListener)
    document.removeEventListener('touchstart', activityListener)
    document.removeEventListener('visibilitychange', activityListener)
    stopIdleWatcher()
})
</script>
